import { Button, Dialog } from '@platform/design-system';
import { errorMessageKey } from '@platform/i18n';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { FileDown, Printer, RotateCcw } from 'lucide-react';
import { useMutation } from '@platform/api-client';
import { printPdf } from '../printPdf.ts';
import { saveDownload } from '../saveDownload.ts';
import { usePageContext } from '../usePageContext.ts';
import type { BulkOutcome, BulkResults } from './bulk.ts';
import { LabelDownload } from './LabelDownload.tsx';
import type { Order } from './orders.ts';
import styles from './Orders.module.css';
import { useHandOffProgress } from './useHandOffProgress.ts';

export type BulkOutcomeSheetProps = {
  outcome: BulkOutcome;
  orders: readonly Order[];
  api: TypedClient;
  shipping: ShippingClient;
  onOpenSettings: () => void;
  onRetryDispatch: (orderIds: readonly string[]) => void;
  onClose: () => void;
};

const numberOf = (orders: readonly Order[], orderId: string): string =>
  orders.find((order) => order.id === orderId)?.number ?? orderId;

export const BulkOutcomeSheet = ({
  outcome,
  orders,
  api,
  shipping,
  onOpenSettings,
  onRetryDispatch,
  onClose,
}: BulkOutcomeSheetProps) => {
  const { t } = usePageContext();
  const skipped =
    outcome.skipped === 0 ? null : (
      <p className={styles['muted']}>{t('orderBulk.skipped', { count: outcome.skipped })}</p>
    );
  return (
    <Dialog
      open
      variant="side"
      title={t(`orderBulk.title_${outcome.kind}`)}
      closeLabel={t('common.close')}
      onClose={onClose}
    >
      {outcome.kind === 'labels' ? (
        <LabelSheetOutcome
          outcome={outcome}
          orders={orders}
          shipping={shipping}
          onOpenSettings={onOpenSettings}
        />
      ) : outcome.kind === 'dispatch' ? (
        <DispatchOutcome results={outcome.results} orders={orders} api={api} onRetry={onRetryDispatch} />
      ) : (
        <StatusOutcome results={outcome.results} orders={orders} />
      )}
      {skipped}
    </Dialog>
  );
};

type Refusal = {
  readonly orderId: string;
  readonly code: string | null;
  readonly carrierMessage: string | null;
};

const refusalsOf = (results: BulkResults): readonly Refusal[] =>
  results.flatMap((result) =>
    result.outcome === 'refused'
      ? [{ orderId: result.orderId, code: result.code, carrierMessage: null }]
      : [],
  );

const handedOffOf = (results: BulkResults): readonly Order[] =>
  results.flatMap((result) => (result.order === null ? [] : [result.order]));

const RefusalList = ({ refusals, orders }: { refusals: readonly Refusal[]; orders: readonly Order[] }) => {
  const { t } = usePageContext();
  return refusals.length === 0 ? null : (
    <section aria-labelledby="bulk-refused">
      <h3 id="bulk-refused">{t('orderBulk.refusedTitle')}</h3>
      <ul className={styles['outcomeList']}>
        {refusals.map((refusal) => (
          <li key={refusal.orderId}>
            <strong>{numberOf(orders, refusal.orderId)}</strong>
            <span className={styles['muted']}>{t(errorMessageKey(refusal.code ?? undefined))}</span>
            {refusal.carrierMessage === null ? null : (
              <span className={styles['muted']}>
                {t('orders.carrierMessage', { message: refusal.carrierMessage })}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

const StatusOutcome = ({ results, orders }: { results: BulkResults; orders: readonly Order[] }) => {
  const { t } = usePageContext();
  const refusals = refusalsOf(results);
  return (
    <>
      <p className={styles['outcomeLine']}>
        <strong>{t('orderBulk.done', { count: results.length - refusals.length })}</strong>
        {refusals.length === 0 ? null : <span>{t('orderBulk.refused', { count: refusals.length })}</span>}
      </p>
      <RefusalList refusals={refusals} orders={orders} />
    </>
  );
};

const DispatchOutcome = ({
  results,
  orders,
  api,
  onRetry,
}: {
  results: BulkResults;
  orders: readonly Order[];
  api: TypedClient;
  onRetry: (orderIds: readonly string[]) => void;
}) => {
  const { t } = usePageContext();
  const progress = useHandOffProgress(api, handedOffOf(results));
  const refusals = [
    ...refusalsOf(results),
    ...progress.refusedByCarrier.map((order) => ({
      orderId: order.id,
      code: order.dispatch.failure,
      carrierMessage: order.dispatch.failureDetail,
    })),
  ];
  const retryable = progress.refusedByCarrier.map((order) => order.id);
  return (
    <>
      <p className={styles['outcomeLine']} aria-live="polite">
        <strong>{t('orderBulk.handedOver', { count: progress.handedOver.length })}</strong>
        {progress.awaiting.length === 0 ? null : (
          <span>{t('orderBulk.awaitingCarrier', { count: progress.awaiting.length })}</span>
        )}
        {refusals.length === 0 ? null : <span>{t('orderBulk.refused', { count: refusals.length })}</span>}
      </p>
      <RefusalList refusals={refusals} orders={[...orders, ...progress.refusedByCarrier]} />
      {retryable.length === 0 ? null : (
        <Button
          variant="outline"
          icon={<RotateCcw size={20} aria-hidden="true" />}
          onClick={() => {
            onRetry(retryable);
          }}
        >
          {t('orderBulk.retryDispatch', { count: retryable.length })}
        </Button>
      )}
    </>
  );
};

const LabelSheetOutcome = ({
  outcome,
  orders,
  shipping,
  onOpenSettings,
}: {
  outcome: Extract<BulkOutcome, { kind: 'labels' }>;
  orders: readonly Order[];
  shipping: ShippingClient;
  onOpenSettings: () => void;
}) => {
  const { t } = usePageContext();
  const printing = useMutation({ mutationFn: printPdf });
  const byShipment = new Map(orders.map((order) => [order.dispatch.shipmentId, order]));
  const withOutcome = (wanted: string) =>
    outcome.sheet.results.filter((result) => result.outcome === wanted).map((result) => result.shipmentId);
  const included = withOutcome('included');
  const separate = withOutcome('separate');
  const listed = (title: string, shipmentIds: readonly string[], id: string) =>
    shipmentIds.length === 0 ? null : (
      <section aria-labelledby={id}>
        <h3 id={id}>{title}</h3>
        <ul className={styles['outcomeList']}>
          {shipmentIds.map((shipmentId) => (
            <li key={shipmentId}>
              <strong>{byShipment.get(shipmentId)?.number ?? shipmentId}</strong>
            </li>
          ))}
        </ul>
      </section>
    );
  const download = outcome.sheet.download;
  return (
    <>
      {download === null ? null : (
        <div className={styles['outcomeLine']}>
          <strong>{t('orderBulk.pdfReady', { count: included.length })}</strong>
          <span className={styles['labelButtons']}>
            <Button
              icon={<Printer size={20} aria-hidden="true" />}
              disabled={printing.isPending}
              onClick={() => {
                printing.mutate(download.url);
              }}
            >
              {printing.isPending ? t('orders.labelPreparing') : t('orderBulk.printPdf')}
            </Button>
            <Button
              variant="ghost"
              icon={<FileDown size={20} aria-hidden="true" />}
              onClick={() => {
                saveDownload(download.url);
              }}
            >
              {t('orderBulk.downloadPdf')}
            </Button>
          </span>
          {printing.isError ? (
            <p role="status" className={styles['muted']}>
              {t('orders.labelPrintFailed')}
            </p>
          ) : null}
        </div>
      )}
      {separate.length === 0 ? null : (
        <section aria-labelledby="bulk-separate">
          <h3 id="bulk-separate">{t('orderBulk.separateTitle')}</h3>
          <ul className={styles['outcomeList']}>
            {separate.map((shipmentId) => (
              <li key={shipmentId}>
                <strong>{byShipment.get(shipmentId)?.number ?? shipmentId}</strong>
                <LabelDownload shipping={shipping} shipmentId={shipmentId} onOpenSettings={onOpenSettings} />
              </li>
            ))}
          </ul>
        </section>
      )}
      {listed(t('orderBulk.notReadyTitle'), withOutcome('not_ready'), 'bulk-not-ready')}
      {listed(t('orderBulk.notFoundTitle'), withOutcome('not_found'), 'bulk-not-found')}
    </>
  );
};
